import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery as useConvexQuery, useConvexAuth } from "convex/react";
import { AppContext } from "./app.context";
import { ModelCxContext } from "./modelCx.context";
import { refs } from "../convex/apiRefs";
import { CONVEX_CONFIGURED } from "../utility/backendConfig";
import { favInboxEnabled, openJobIds, buildGhostsByCell, adviceAsInboxRow, buildSkipsByJob } from "../utility/favInbox";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FAV Inbox (#630) — the current office's open Inbox rows, live from Convex, shared
// by the left pane's Inbox tab and every JobTile (which dims the jobs in it).
//
// Works for mongoOnly users too: the app-wide ConvexProvider is mounted whenever
// Convex is configured, and this subscription is what opens its websocket.
//
// A refused/failed Convex query throws during render, and with no boundary that
// unmounts the WHOLE app (pattern_convex_arg_mismatch_blanks_the_app). Here a failure
// just switches the Inbox off (no tab, no dimming) and the app carries on.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const EMPTY = { enabled: false, loading: false, rows: [], adviceRows: [], cancelledRows: [], openIds: new Set(), ghostsByCell: new Map(), skipsByJob: new Map(), jumpRequest: null, requestJump: () => {} };

const JUMP_FIND_MS = 20000; // the new week's data can take a few seconds to load
const FLASH_MS = 2600;

/**
 * After a jump, wait for the job's tile(s) on the grid (never the Inbox's own card),
 * scroll the first into view and flash them all. Returns a cancel function.
 */
export function focusJobTiles(jobId, doc = document) {
  const started = Date.now();
  let timer = null;
  let flashTimer = null;
  const find = () =>
    [...doc.querySelectorAll(`[data-job-id="${jobId}"], [data-fav-ghost="${jobId}"]`)].filter(
      (el) => !el.closest('[data-testid="fav-inbox"]')
    );
  const tick = () => {
    const tiles = find();
    if (tiles.length) {
      tiles[0].scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
      // Keep flashing anything for this job that appears DURING the flash — after a week
      // jump the crew rows (and their ghost cards) can render a moment after the real
      // card (seen in mongoOnly on UAT -07).
      const flashed = new Set();
      const flashNew = () => find().forEach((el) => {
        if (!flashed.has(el)) {
          flashed.add(el);
          el.setAttribute("data-fav-flash", "on");
        }
      });
      flashNew();
      const flashStart = Date.now();
      const catchLate = () => {
        if (Date.now() - flashStart >= FLASH_MS) {
          flashed.forEach((el) => el.removeAttribute("data-fav-flash"));
          return;
        }
        flashNew();
        flashTimer = setTimeout(catchLate, 150);
      };
      flashTimer = setTimeout(catchLate, 150);
      return;
    }
    if (Date.now() - started < JUMP_FIND_MS) timer = setTimeout(tick, 250);
  };
  tick();
  return () => {
    clearTimeout(timer);
    clearTimeout(flashTimer);
  };
}

export const FavInboxContext = createContext(EMPTY);

// The subscription lives in its OWN tiny component inside its OWN boundary, so the
// boundary catches only a failing Inbox query — never an unrelated error elsewhere
// in the app.
function FavInboxFetcher({ onRows, onAdvice }) {
  const { companyCode, locationFilter } = useContext(AppContext);
  const { isAuthenticated } = useConvexAuth();
  const ready = isAuthenticated && !!companyCode && !!locationFilter;
  const rows = useConvexQuery(
    refs.favInboxList,
    ready ? { companyCode, location: locationFilter } : "skip"
  );
  useEffect(() => {
    onRows(ready ? rows : []); // undefined = still loading
  }, [ready, rows, onRows]);
  // FAV 2.0 advice-only proposals (#652, TEMPORARY): jobs FileMaker asked about with
  // reserve:false — not booked — for the "Unscheduled" accordion and their ghosts.
  const advice = useConvexQuery(refs.fav2Advice, ready ? { companyCode, location: locationFilter } : "skip");
  useEffect(() => {
    onAdvice(ready && advice ? advice : []);
  }, [ready, advice, onAdvice]);
  return null;
}

// #755: the jobs FAV 2.0 couldn't schedule (no crew, no date), for the red box + ⓘ on their
// tile. Its own component AND its own boundary (below): if this query fails — e.g. a build
// that is ahead of the Convex box — only the red boxes go; the Inbox carries on.
function FavSkipsFetcher({ onSkips }) {
  const { companyCode, locationFilter } = useContext(AppContext);
  const { isAuthenticated } = useConvexAuth();
  const ready = isAuthenticated && !!companyCode && !!locationFilter;
  const skips = useConvexQuery(refs.fav2Skips, ready ? { companyCode, location: locationFilter } : "skip");
  useEffect(() => {
    onSkips(ready && skips ? skips : []);
  }, [ready, skips, onSkips]);
  return null;
}

class FavInboxBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    console.warn(`%cFavInbox: Convex query failed — ${this.props.what || "Inbox"} hidden`, "color: #f97316; font-weight: bold;", error);
    this.props.onFail();
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function FavInboxProvider({ children }) {
  const { tsUserDocument } = useContext(ModelCxContext);
  const enabled = favInboxEnabled({ appEnv: process.env.REACT_APP_ENV, convexConfigured: CONVEX_CONFIGURED, inboxFlag: tsUserDocument?.inboxFlag });
  const [rows, setRows] = useState(undefined);
  const [advice, setAdvice] = useState([]);
  const [failed, setFailed] = useState(false);
  const onFail = useCallback(() => setFailed(true), []);
  const [skips, setSkips] = useState([]);
  const [skipsFailed, setSkipsFailed] = useState(false);
  const onSkipsFail = useCallback(() => setSkipsFailed(true), []);
  // Clicking an Inbox booking: the screens move their grid to the booked week
  // (scheduleScreen / optimizerScreen listen for jumpRequest), then focusJobTiles
  // scrolls to the tile and flashes it.
  const [jumpRequest, setJumpRequest] = useState(null);
  const requestJump = useCallback(
    (row) => setJumpRequest({ jobId: row.jobId, bookedDate: row.bookedDate, seq: Date.now() }),
    []
  );
  useEffect(() => (jumpRequest ? focusJobTiles(jumpRequest.jobId) : undefined), [jumpRequest]);
  const value = useMemo(() => {
    if (!enabled || failed) return EMPTY;
    const adviceRows = (advice || []).map(adviceAsInboxRow);
    // Customer-cancelled bookings (FileMaker took them back to RTS) are listed for their
    // FAV 2.0 answer only — they're off the grid, so they never dim a tile or draw a ghost.
    const liveRows = (rows || []).filter((r) => r.status !== "customerCancelled");
    const cancelledRows = (rows || []).filter((r) => r.status === "customerCancelled");
    return {
      enabled: true,
      loading: rows === undefined,
      rows: liveRows,
      cancelledRows,
      adviceRows, // TEMPORARY "Unscheduled" accordion (#652)
      openIds: openJobIds(liveRows), // only BOOKED jobs dim — advice / cancelled jobs aren't on the grid
      ghostsByCell: buildGhostsByCell([...liveRows, ...adviceRows]), // FAV 2.0 ghosts (#642, #652)
      skipsByJob: skipsFailed ? new Map() : buildSkipsByJob(skips), // #755 red box + ⓘ
      jumpRequest,
      requestJump,
    };
  }, [enabled, failed, rows, advice, skips, skipsFailed, jumpRequest, requestJump]);
  return (
    <FavInboxContext.Provider value={value}>
      {enabled && !failed && (
        <FavInboxBoundary onFail={onFail}>
          <FavInboxFetcher onRows={setRows} onAdvice={setAdvice} />
        </FavInboxBoundary>
      )}
      {enabled && !failed && !skipsFailed && (
        <FavInboxBoundary onFail={onSkipsFail} what="FAV 2.0 red boxes">
          <FavSkipsFetcher onSkips={setSkips} />
        </FavInboxBoundary>
      )}
      {children}
    </FavInboxContext.Provider>
  );
}
